import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  Loader2,
  Plus,
  ShieldAlert,
  X,
} from "lucide-react";

import { supabase, getCurrentSession } from "../lib/supabase";
import {
  buildInstallCommand,
  extractFunctionErrorMessage,
} from "../lib/agentInstall";
import { cn } from "../lib/utils";
import { AgentToolIcon } from "./brand/AgentToolIcon";
import { ProjectRulesNudge } from "./ProjectRulesNudge";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Skeleton } from "./ui/skeleton";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface InstallToken {
  id: string;
  client_id: string;
  client_name: string;
  agent_name: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  project_id: string | null;
  // 2026-09-17: added so "pending" can mean "install command not run yet"
  // instead of "no check-in yet" -- see pendingInstalls/awaitingCheckinTokens
  // below. Set by claim-install-token the moment install.sh actually runs,
  // well before any real check-in.
  claimed_at: string | null;
}

interface AgentRow {
  id: string;
  name: string;
  client_id: string | null;
  client_name: string | null;
  created_at: string;
  project_id: string | null;
}

interface ProjectOption {
  id: string;
  name: string;
}

interface AgentInstallManagerProps {
  teamId: string;
  className?: string;
  defaultExpanded?: boolean;
  title?: string;
  description?: string;
  // 2026-10-03: renders just the mint-a-new-agent flow (name + project +
  // generate/copy command + setup steps) with no outer Card chrome and no
  // Active/Pending management grids -- built for embedding in the "Add
  // agent" Sheet on TeamAgents.tsx. As of 2026-10-08 that Sheet renders
  // AgentConnectGrid + ConnectAgentModal instead (see TeamAgents.tsx's
  // header comment), so no caller currently passes `compact` -- left here
  // rather than removed in case this embed point is wanted again; the full
  // manage view (this component's default) stays unchanged for
  // InstallClaudeCode.tsx either way.
  compact?: boolean;
}

const formatTimestamp = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "Never used";

const AgentInstallManager: React.FC<AgentInstallManagerProps> = ({
  teamId,
  className,
  defaultExpanded = false,
  title = "Manage Claude Code connectors",
  description = "Add more connectors, keep install instructions available, and see new installs go active as soon as they run.",
  compact = false,
}) => {
  const [tokens, setTokens] = useState<InstallToken[]>([]);
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [minting, setMinting] = useState(false);
  // The one-time claim code (NOT the real install token — see
  // buildInstallCommand's header comment). Single-use and dead in ~10
  // minutes, redeemed by install.sh via claim-install-token.
  const [mintedClaimCode, setMintedClaimCode] = useState<string | null>(null);
  const [agentNameInput, setAgentNameInput] = useState("");
  // All of this team's projects — needed to resolve project NAMES for every
  // agent/token card below, regardless of whose pod they belong to (an
  // admin viewing this page sees agents across every pod, not just their
  // own). Do not restrict this list to the viewer's pod.
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  // Only the projects assigned (via project_pods) to the CURRENT VIEWER's
  // own pod — this is what actually populates the "Add agent" dropdown, per
  // 2026-09-09 decision: a member should only be able to pick a project
  // their team (pod) has been assigned, not every project in the whole
  // Slack workspace.
  const [myPodProjects, setMyPodProjects] = useState<ProjectOption[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState("unassigned");
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [tokensResult, agentsResult, projectsResult] = await Promise.all([
      (supabase.from as any)("hook_install_tokens")
        .select(
          "id, client_id, client_name, agent_name, created_at, last_used_at, revoked_at, project_id, claimed_at",
        )
        .eq("team_id", teamId)
        .is("revoked_at", null)
        .order("created_at", { ascending: false }),
      (supabase.from as any)("agents")
        .select("id, name, client_id, client_name, created_at, project_id")
        .eq("team_id", teamId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false }),
      (supabase.from as any)("projects")
        .select("id, name")
        .eq("team_id", teamId)
        .is("deleted_at", null)
        .order("name", { ascending: true }),
    ]);

    if (tokensResult.error || agentsResult.error || projectsResult.error) {
      console.error(
        "Failed to load agent installs:",
        tokensResult.error ?? agentsResult.error ?? projectsResult.error,
      );
      setError("Could not load agent installs.");
      setLoading(false);
      return;
    }

    setTokens((tokensResult.data ?? []) as InstallToken[]);
    setAgents((agentsResult.data ?? []) as AgentRow[]);
    const allProjects = (projectsResult.data ?? []) as ProjectOption[];
    setProjects(allProjects);

    // Scope the "Add agent" dropdown to the viewer's own pod: no pod (never
    // assigned one, or an older account from before pods existed) and no
    // projects assigned to that pod both resolve to the same empty result,
    // per 2026-09-09 decision — never fall back to showing every team
    // project, that would defeat the point of scoping by pod at all.
    const {
      data: { user: authUser },
    } = await supabase.auth.getUser();

    let podScopedProjects: ProjectOption[] = [];
    if (authUser) {
      const { data: userRow, error: userRowError } = await (
        supabase.from as any
      )("users")
        .select("pod_id")
        .eq("auth_user_id", authUser.id)
        .eq("team_id", teamId)
        .maybeSingle();

      const podId = userRowError
        ? null
        : ((userRow?.pod_id as string | null | undefined) ?? null);

      if (podId) {
        const { data: podProjectRows, error: podProjectsError } = await (
          supabase.from as any
        )("project_pods")
          .select("project_id")
          .eq("pod_id", podId);

        if (!podProjectsError) {
          const allowedProjectIds = new Set(
            (podProjectRows ?? []).map(
              (row: { project_id: string }) => row.project_id,
            ),
          );
          podScopedProjects = allProjects.filter((p) =>
            allowedProjectIds.has(p.id),
          );
        }
      }
    }

    setMyPodProjects(podScopedProjects);
    setLoading(false);
  }, [teamId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const agentIdsByClient = useMemo(() => {
    const map = new Map<string, AgentRow>();
    for (const agent of agents) {
      if (agent.client_id) map.set(agent.client_id, agent);
    }
    return map;
  }, [agents]);

  // 2026-09-17: split what used to be one "no agent row yet" bucket into
  // two real states -- see the InstallToken.claimed_at comment. A token
  // that's been claimed (install.sh actually ran) now counts as connected
  // even before its first check-in, same signal change made in
  // WelcomeModal.tsx/ConnectAgentModal.tsx, so "pending" here means the
  // same thing it does in onboarding: the command was minted but nobody's
  // run it yet.
  const pendingInstalls = useMemo(
    () =>
      tokens.filter(
        (token) => !agentIdsByClient.has(token.client_id) && !token.claimed_at,
      ),
    [tokens, agentIdsByClient],
  );

  const awaitingCheckinTokens = useMemo(
    () =>
      tokens.filter(
        (token) => !agentIdsByClient.has(token.client_id) && !!token.claimed_at,
      ),
    [tokens, agentIdsByClient],
  );

  const activeAgents = useMemo(
    () =>
      agents.map((agent) => ({
        ...agent,
        installToken: agent.client_id
          ? (tokens.find((token) => token.client_id === agent.client_id) ??
            null)
          : null,
      })),
    [agents, tokens],
  );

  const activeCommand = mintedClaimCode
    ? buildInstallCommand(mintedClaimCode)
    : "";

  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of projects) map.set(p.id, p.name);
    return map;
  }, [projects]);

  const handleGenerate = useCallback(async () => {
    setExpanded(true);
    setMinting(true);
    setError(null);
    setCopyState("idle");

    // mint-install-token has verify_jwt=true and reads ctx.jwtClaims.sub, so
    // it needs the caller's actual session token — supabase-js's invoke()
    // does NOT attach this automatically (every other authenticated
    // invoke() call in this codebase — manage-agents, manage-pods,
    // get-all-team-users, etc. — passes it explicitly for the same reason).
    // Without it, the function only ever sees the anon key, which has no
    // `sub` claim, and always 401s regardless of whether the member is
    // actually signed in (confirmed via PROD logs: every mint-install-token
    // call had 401'd this way since the very first attempt).
    const session = await getCurrentSession();
    if (!session) {
      setError(
        "Your session has expired. Please refresh the page and sign in again.",
      );
      setMinting(false);
      return;
    }

    const { data, error: invokeError } = await supabase.functions.invoke(
      "mint-install-token",
      {
        body: {
          team_id: teamId,
          agent_name: agentNameInput.trim() || undefined,
          project_id:
            selectedProjectId !== "unassigned" ? selectedProjectId : undefined,
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      },
    );

    if (invokeError) {
      setError(await extractFunctionErrorMessage(invokeError));
      setMinting(false);
      return;
    }

    const claimCode = data?.data?.claim_code as string | undefined;
    if (!claimCode) {
      setError("The server did not return a claim code. Please try again.");
      setMinting(false);
      return;
    }

    setMintedClaimCode(claimCode);
    setAgentNameInput("");
    setSelectedProjectId("unassigned");
    setMinting(false);
    await loadData();
  }, [agentNameInput, selectedProjectId, loadData, teamId]);

  const handleCopy = async () => {
    if (!mintedClaimCode) return;
    await navigator.clipboard.writeText(activeCommand);
    setCopyState("copied");
    setTimeout(() => setCopyState("idle"), 2000);
  };

  const handleRevoke = async (id: string) => {
    setRevokingId(id);
    const { error: revokeError } = await (supabase.from as any)(
      "hook_install_tokens",
    )
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", id);

    if (revokeError) {
      console.error("Failed to revoke token:", revokeError);
      setError("Could not revoke that token. Please try again.");
    } else {
      setTokens((prev) => prev.filter((token) => token.id !== id));
    }
    setRevokingId(null);
  };

  if (compact) {
    return (
      <div className={cn("space-y-6", className)}>
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Connector name
          </label>
          <Input
            value={agentNameInput}
            onChange={(e) => setAgentNameInput(e.target.value)}
            placeholder="e.g, Deploy-Bot"
            maxLength={60}
              aria-label="Connector name"
          />
        </div>

        {/* <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Project</label>
          <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
            <SelectTrigger aria-label="Project">
              <SelectValue placeholder="Project (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">No project</SelectItem>
              {myPodProjects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!loading && myPodProjects.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No projects are assigned to your team yet — ask an admin to assign one from the
              Projects page before you can attach a new agent to a project.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Only a fallback for usage a rule doesn't already catch — see the tip below if this
              agent works across more than one project.
            </p>
          )}
        </div> */}

        {mintedClaimCode && <ProjectRulesNudge teamId={teamId} />}

        {mintedClaimCode ? (
          <div className="space-y-4 border-t border-border pt-4">
            <Alert>
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription>
                This claim code is single-use and expires in about 10 minutes —
                it is not your install token, so it's safe to leave visible
                while you copy it.
              </AlertDescription>
            </Alert>
            <Textarea
              value={activeCommand}
              readOnly
              rows={3}
              className="font-mono text-xs leading-5"
              aria-label="Claude Code install command"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={handleCopy} className="gap-1.5">
                <Copy className="h-4 w-4" />
                {copyState === "copied" ? "Copied!" : "Copy install command"}
              </Button>
              <Button
                variant="outline"
                onClick={handleGenerate}
                disabled={minting}
              >
                Generate another
              </Button>
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                Next steps
              </h3>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground">
                <li>
                  Copy the command above and run it in your own terminal, in the
                  project you want to connect.
                </li>
                <li>
                  Restart Claude Code after install (
                  <code className="text-xs">/exit</code> then{" "}
                  <code className="text-xs">claude</code>) so the Stop hook
                  loads for that session.
                </li>
                <li>
                  Come back to the Agents page to see it move from pending to
                  active — that happens as soon as the command finishes running,
                  before its first check-in.
                </li>
              </ol>
            </div>
          </div>
        ) : (
          <Button
            onClick={handleGenerate}
            disabled={minting}
            className="w-full gap-1.5"
          >
            {minting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Generate install command
              </>
            )}
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card className={cn(className)}>
      <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-xl text-foreground">{title}</CardTitle>
            <Badge variant="secondary">
              {activeAgents.length + awaitingCheckinTokens.length} active
            </Badge>
            <Badge variant="outline">{pendingInstalls.length} pending</Badge>
          </div>
          <CardDescription className="max-w-2xl">{description}</CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={agentNameInput}
            onChange={(e) => setAgentNameInput(e.target.value)}
              placeholder="Name this connector (optional)"
            maxLength={60}
            className="h-9 w-full sm:w-52"
              aria-label="Connector name"
          />
          <Select
            value={selectedProjectId}
            onValueChange={setSelectedProjectId}
          >
            <SelectTrigger className="h-9 w-full sm:w-48" aria-label="Project">
              <SelectValue placeholder="Project (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">No project</SelectItem>
              {myPodProjects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleGenerate} disabled={minting}>
            {minting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                Add connector
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
          >
            {expanded ? (
              <>
                Hide setup
                <ChevronUp className="ml-2 h-4 w-4" />
              </>
            ) : (
              <>
                Show setup
                <ChevronDown className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      {!loading && myPodProjects.length === 0 && (
        <div className="px-6">
          <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            No projects are assigned to your team yet — ask an admin to assign
            one from the Projects page before you can attach a new agent to a
            project.
          </p>
        </div>
      )}

      <CardContent className="space-y-6">
        <ProjectRulesNudge teamId={teamId} />

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          // Mirrors the two-column Active/Pending grid below: a heading line
          // plus a couple of agent-card placeholders per column (icon,
          // name/meta lines, status pill).
          <div className="grid gap-4 lg:grid-cols-2">
            {[0, 1].map((col) => (
              <div key={col} className="space-y-3">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-40" />
                </div>
                <div className="space-y-3">
                  {[0, 1].map((row) => (
                    <div key={row} className="border border-border p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <Skeleton className="mt-0.5 h-5 w-5 shrink-0 rounded-md" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-40" />
                            <Skeleton className="h-3 w-28" />
                          </div>
                        </div>
                        <Skeleton className="h-5 w-16 shrink-0 rounded-full" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  Active agents
                </h3>
                <span className="text-xs text-muted-foreground">
                  Installs completed — connected, whether or not they've checked
                  in yet
                </span>
              </div>
              {activeAgents.length === 0 &&
              awaitingCheckinTokens.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/50 p-4 text-sm text-muted-foreground">
                  No active connectors yet. Click{" "}
                  <span className="font-medium">Add connector</span> to mint a new
                  install command.
                </div>
              ) : (
                <div className="space-y-3">
                  {/* No border-radius, per Figma (2026-09-29) -- was rounded-lg. */}
                  {activeAgents.map((agent) => (
                    <div
                      key={agent.id}
                      className="border border-success/30 bg-success/10 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <AgentToolIcon
                            clientName={agent.client_name ?? agent.name}
                            className="mt-0.5 h-5 w-5 shrink-0"
                          />
                          <div>
                            {/* Member-chosen name takes priority over client_name
                                (the tool type, e.g. "Claude Code") — previously
                                this read client_name first, so every agent
                                displayed as "Claude Code" regardless of what it
                                was actually named. */}
                            <p className="font-medium text-foreground">
                              {agent.name ?? agent.client_name ?? "Claude Code"}
                            </p>
                            {agent.name &&
                              agent.client_name &&
                              agent.name !== agent.client_name && (
                                <p className="text-xs text-muted-foreground">
                                  {agent.client_name}
                                </p>
                              )}
                            <p className="text-xs text-muted-foreground">
                              Agent created {formatTimestamp(agent.created_at)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Project:{" "}
                              {agent.project_id
                                ? (projectNameById.get(agent.project_id) ??
                                  "Unknown project")
                                : "None — set one from this agent’s detail page"}
                            </p>
                            {agent.client_id && (
                              <p className="mt-1 break-all text-[11px] text-muted-foreground/80">
                                Client ID: {agent.client_id}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge variant="success">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Active
                        </Badge>
                      </div>
                      <p className="mt-3 text-xs text-muted-foreground">
                        {agent.installToken?.last_used_at
                          ? `Last used ${formatTimestamp(agent.installToken.last_used_at)}`
                          : "Waiting for the next check-in to refresh last-used time."}
                      </p>
                    </div>
                  ))}
                  {/* Claimed (install.sh ran) but no agent row yet -- still
                      "Active" per the 2026-09-17 signal change above, just
                      without any real usage data to show. Revoke stays
                      available here (unlike a fully-active agent, which has
                      no revoke control at all) since this is still the
                      install token, not an agent record yet. */}
                  {/* No border-radius, per Figma (2026-09-29) -- was rounded-lg. */}
                  {awaitingCheckinTokens.map((token) => (
                    <div
                      key={token.id}
                      className="border border-success/30 bg-success/10 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <AgentToolIcon
                            clientName={token.client_name}
                            className="mt-0.5 h-5 w-5 shrink-0"
                          />
                          <div>
                            <p className="font-medium text-foreground">
                              {token.agent_name || token.client_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Connected {formatTimestamp(token.claimed_at)}
                            </p>
                            {token.project_id && (
                              <p className="text-xs text-muted-foreground">
                                Project:{" "}
                                {projectNameById.get(token.project_id) ??
                                  "Unknown project"}
                              </p>
                            )}
                            <p className="mt-1 break-all text-[11px] text-muted-foreground/80">
                              Client ID: {token.client_id}
                            </p>
                          </div>
                        </div>
                        <Badge variant="success">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Active
                        </Badge>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <p className="text-xs text-muted-foreground">
                          {token.last_used_at
                            ? `Last used ${formatTimestamp(token.last_used_at)}`
                            : "Waiting for the first check-in to report activity."}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRevoke(token.id)}
                          disabled={revokingId === token.id}
                        >
                          {revokingId === token.id ? "Revoking…" : "Revoke"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  Pending installs
                </h3>
                <span className="text-xs text-muted-foreground">
                  Minted install commands nobody has run yet
                </span>
              </div>
              {pendingInstalls.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/50 p-4 text-sm text-muted-foreground">
                  No pending installs right now.
                </div>
              ) : (
                <div className="space-y-3">
                  {/* No border-radius, per Figma (2026-09-29) -- was rounded-lg. */}
                  {pendingInstalls.map((token) => (
                    <div
                      key={token.id}
                      className="border border-chart-1/40 bg-chart-1/10 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <AgentToolIcon
                            clientName={token.client_name}
                            className="mt-0.5 h-5 w-5 shrink-0"
                          />
                          <div>
                            <p className="font-medium text-foreground">
                              {token.agent_name || token.client_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Created {formatTimestamp(token.created_at)}
                            </p>
                            {token.project_id && (
                              <p className="text-xs text-muted-foreground">
                                Project:{" "}
                                {projectNameById.get(token.project_id) ??
                                  "Unknown project"}
                              </p>
                            )}
                            <p className="mt-1 break-all text-[11px] text-muted-foreground/80">
                              Client ID: {token.client_id}
                            </p>
                          </div>
                        </div>
                        <Badge
                          variant="outline"
                          className="border-chart-1/50 text-chart-1"
                        >
                          Pending
                        </Badge>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <p className="text-xs text-muted-foreground">
                          Waiting for the install command to be run.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRevoke(token.id)}
                          disabled={revokingId === token.id}
                        >
                          {revokingId === token.id ? "Revoking…" : "Revoke"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {expanded && (
          // No border-radius, per Figma (2026-09-29) -- was rounded-xl.
          <div className="space-y-4 border border-border bg-muted/50 p-4">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                Claude Code setup
              </h3>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground">
                <li>
                  Click <span className="font-medium">Add connector</span> to mint a
                  one-time install command for this team.
                </li>
                <li>
                  Copy the command and run it in your own terminal, in the
                  project you want to connect.
                </li>
                <li>
                  Restart Claude Code after install (
                  <code className="text-xs">/exit</code> then{" "}
                  <code className="text-xs">claude</code>) so the Stop hook
                  loads for that session.
                </li>
                <li>
                  Come back here to confirm the new install moves from pending
                  to active — that happens as soon as the command finishes
                  running, before its first check-in.
                </li>
              </ol>
            </div>

            {mintedClaimCode ? (
              <div className="space-y-3">
                <Alert>
                  <ShieldAlert className="h-4 w-4" />
                  <AlertDescription>
                    This claim code is single-use and expires in about 10
                    minutes — it is not your install token, so it's safe to
                    leave visible while you copy it. Dismiss it when you are
                    done, or mint another for a new install.
                  </AlertDescription>
                </Alert>
                <div className="flex flex-wrap items-center gap-2">
                  <Button onClick={handleCopy}>
                    <Copy className="mr-2 h-4 w-4" />
                    {copyState === "copied"
                      ? "Copied!"
                      : "Copy install command"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setMintedClaimCode(null)}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Dismiss command
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleGenerate}
                    disabled={minting}
                  >
                    Generate another
                  </Button>
                </div>
                <Textarea
                  value={activeCommand}
                  readOnly
                  rows={3}
                  className="font-mono text-xs leading-5"
                  aria-label="Claude Code install command"
                />
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border bg-card p-4 text-sm text-muted-foreground">
                Install instructions stay available here. Mint a fresh command
                whenever you want to connect another Claude Code session.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentInstallManager;
